/**
 * Agriculture Student Notes - Years 11-13
 * Based on Vanuatu National Syllabus
 * Comprehensive study materials for all strands
 */

// Year 11 Agriculture Student Notes
export const year11AgricultureStudentNotes = {
  strand1: {
    title: "Sustainable Farm Ecosystem",
    overview: "Understanding natural and farm ecosystems, agricultural management systems, and impacts of agriculture on ecosystems",
    image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Natural and Farm Ecosystems",
        content: `
## Natural and Farm Ecosystems

### Key Definitions
- **Natural Ecosystem**: A community of living organisms interacting with their physical environment, existing without human interference
- **Farm Ecosystem**: An artificial ecosystem created and managed by humans for agricultural production
- **Biodiversity**: The variety of plant and animal life in a particular habitat

### Comparison of Ecosystems

| Feature | Natural Ecosystem | Farm Ecosystem |
|---------|------------------|----------------|
| Sustainability | Self-sustaining | Requires human input |
| Diversity | High biodiversity | Low biodiversity (monoculture) |
| Purpose | Natural balance | Food/fiber production |
| Nutrient Cycling | Complete cycle | Often interrupted |
| Energy Source | Sun (photosynthesis) | Sun + external inputs |

### Features of Natural Ecosystems
1. **Producers** - Plants that convert sunlight into food
2. **Consumers** - Animals that eat plants or other animals
3. **Decomposers** - Organisms that break down dead matter
4. **Abiotic factors** - Non-living elements (water, air, soil, sunlight)

### Features of Farm Ecosystems
1. **Crops** - Cultivated plants for food/fiber
2. **Livestock** - Animals raised for production
3. **Managed inputs** - Fertilizers, water, pesticides
4. **Human labor** - Essential for maintenance

### The Farmer's Influence
- Controls species selection
- Manages soil fertility
- Regulates water supply
- Controls pests and diseases
- Determines harvest timing
`,
        keyTerms: [
          { term: "Ecosystem", definition: "A biological community of interacting organisms and their physical environment" },
          { term: "Biodiversity", definition: "The variety of life forms within a given ecosystem" },
          { term: "Sustainability", definition: "The ability to maintain ecological balance over time" },
          { term: "Abiotic", definition: "Non-living components of an ecosystem (water, air, minerals)" },
          { term: "Biotic", definition: "Living components of an ecosystem (plants, animals, microorganisms)" }
        ],
        practiceQuestions: [
          "Compare and contrast natural and farm ecosystems",
          "Explain why farm ecosystems are not self-sustaining",
          "Describe three ways a farmer influences the farm ecosystem"
        ],
        examTips: ["Use diagrams to show ecosystem interactions", "Always provide specific examples from Vanuatu"]
      },
      {
        id: "unit1.2",
        title: "Agricultural Management Systems",
        content: `
## Agricultural Management Systems

### Types of Farming Systems

#### 1. Mono-cropping
Growing a single crop species in a field at a time
- **Advantages**: Easier management, specialized equipment, economies of scale
- **Disadvantages**: Soil depletion, pest buildup, disease vulnerability

#### 2. Multi-cropping
Growing two or more crops in the same field simultaneously or sequentially
- **Advantages**: Better land use, risk reduction, soil improvement
- **Disadvantages**: Complex management, competition for resources

#### 3. Organic Farming
Production without synthetic chemicals, using natural inputs only
- **Features**:
  - No synthetic fertilizers or pesticides
  - Use of compost and manure
  - Crop rotation
  - Biological pest control

#### 4. Agro-forestry
Combining trees with crops and/or livestock on the same land
- **Benefits**:
  - Soil protection
  - Biodiversity enhancement
  - Multiple income sources
  - Carbon sequestration

### Diversification in Agriculture
**Why diversify?**
- Reduces risk from crop failure
- Improves soil health through rotation
- Provides multiple income streams
- Enhances food security
- Supports ecological balance

### Economic Importance
| System | Initial Cost | Long-term Profitability | Environmental Impact |
|--------|-------------|------------------------|---------------------|
| Mono-cropping | Low | Variable | Negative |
| Multi-cropping | Medium | Stable | Positive |
| Organic | High | High | Positive |
| Agro-forestry | High | Very High | Very Positive |
`,
        keyTerms: [
          { term: "Mono-cropping", definition: "Growing a single crop species in a field" },
          { term: "Multi-cropping", definition: "Growing multiple crops in the same area" },
          { term: "Organic farming", definition: "Farming without synthetic chemicals" },
          { term: "Agro-forestry", definition: "Combining trees with agricultural crops" },
          { term: "Diversification", definition: "Varying farm activities to reduce risk" }
        ],
        practiceQuestions: [
          "List four features of organic farming",
          "Explain the ecological benefits of agro-forestry",
          "Discuss why diversification is important for small-scale farmers in Vanuatu"
        ],
        examTips: ["Give examples relevant to Vanuatu agriculture", "Discuss both advantages and disadvantages"]
      },
      {
        id: "unit1.3",
        title: "Impacts of Agriculture on Ecosystems",
        content: `
## Impacts of Agriculture on Ecosystems

### Harmful Agricultural Practices

#### 1. Soil Erosion
- Caused by: Overgrazing, deforestation, improper tillage
- Effects: Loss of topsoil, reduced fertility, sedimentation

#### 2. Water Pollution
- Sources: Fertilizer runoff, pesticides, animal waste
- Effects: Eutrophication, harm to aquatic life, unsafe drinking water

#### 3. Deforestation
- Causes: Land clearing for crops and pasture
- Effects: Habitat loss, climate change, soil degradation

#### 4. Soil Degradation
- Types: Compaction, salinization, acidification
- Causes: Overuse, improper irrigation, chemical buildup

### Environmental Consequences in Vanuatu

| Practice | Short-term Effect | Long-term Effect |
|----------|------------------|------------------|
| Slash and burn | Quick fertility boost | Soil depletion, erosion |
| Overgrazing | Vegetation loss | Desertification |
| Pesticide use | Pest control | Soil/water contamination |
| Monoculture | High yields | Biodiversity loss |

### Future Predictions
Without sustainable practices:
- Increased soil erosion
- Water scarcity
- Loss of native species
- Reduced agricultural productivity
- Climate change acceleration

### Sustainable Solutions
1. Conservation tillage
2. Cover cropping
3. Integrated pest management
4. Agroforestry adoption
5. Water conservation techniques
`,
        keyTerms: [
          { term: "Soil erosion", definition: "The wearing away of topsoil by water, wind, or tillage" },
          { term: "Eutrophication", definition: "Excessive nutrient enrichment in water bodies causing algae blooms" },
          { term: "Deforestation", definition: "The clearing of forests for other land uses" },
          { term: "Salinization", definition: "Buildup of salts in soil from improper irrigation" },
          { term: "Desertification", definition: "Land degradation in arid areas due to climate and human activities" }
        ],
        practiceQuestions: [
          "Identify three agricultural practices harmful to natural ecosystems",
          "Explain the effects of soil erosion on farm productivity",
          "Predict the long-term impacts of slash and burn farming on Vanuatu's ecosystems"
        ],
        examTips: ["Link causes to effects clearly", "Suggest sustainable alternatives"]
      }
    ]
  },
  strand2: {
    title: "Soil Management",
    overview: "Understanding soil properties, management practices, and conservation techniques",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Properties of Soils",
        content: `
## Properties of Soils

### Physical Properties

#### Soil Texture
The relative proportions of sand, silt, and clay particles

| Particle | Size | Feel |
|----------|------|------|
| Sand | 0.05-2mm | Gritty |
| Silt | 0.002-0.05mm | Smooth |
| Clay | <0.002mm | Sticky |

**Soil Types:**
- Sandy soil - Good drainage, low fertility
- Clay soil - Poor drainage, high fertility
- Loam soil - Ideal balance of all three

#### Soil Structure
The arrangement of soil particles into aggregates
- **Granular** - Best for agriculture
- **Blocky** - Common in subsoils
- **Platy** - Poor water infiltration
- **Prismatic** - Found in clay-rich soils

### Chemical Properties

#### Soil pH
- Scale: 0-14 (7 is neutral)
- Most crops prefer 6.0-7.0
- Affects nutrient availability

**Essential Nutrients:**
| Nutrient | Symbol | Function |
|----------|--------|----------|
| Nitrogen | N | Leaf growth |
| Phosphorus | P | Root development |
| Potassium | K | Disease resistance |
| Calcium | Ca | Cell wall strength |
| Magnesium | Mg | Chlorophyll production |

### Biological Properties

#### Soil Organisms
1. **Bacteria** - Decomposition, nitrogen fixation
2. **Fungi** - Nutrient cycling, plant symbiosis
3. **Earthworms** - Soil aeration, organic matter mixing
4. **Protozoa** - Nutrient release

#### Nitrogen Fixation by Rhizobia
- Bacteria living in root nodules of legumes
- Convert atmospheric N₂ to plant-usable NH₄⁺
- Important for sustainable agriculture

### Deficiency Symptoms

| Nutrient | Deficiency Symptoms |
|----------|-------------------|
| Nitrogen | Yellowing of older leaves |
| Phosphorus | Purple coloring, stunted growth |
| Potassium | Brown leaf edges, weak stems |
| Iron | Yellowing between leaf veins |
`,
        keyTerms: [
          { term: "Soil texture", definition: "The proportion of sand, silt, and clay in soil" },
          { term: "Soil structure", definition: "The arrangement of soil particles into aggregates" },
          { term: "Soil pH", definition: "A measure of soil acidity or alkalinity" },
          { term: "Rhizobia", definition: "Bacteria that fix atmospheric nitrogen in legume root nodules" },
          { term: "Loam", definition: "Soil with balanced proportions of sand, silt, and clay" }
        ],
        practiceQuestions: [
          "Describe the difference between soil texture and soil structure",
          "Explain the role of Rhizobia bacteria in nitrogen fixation",
          "List three deficiency symptoms in plants and their causes"
        ],
        examTips: ["Learn to identify soil types by texture", "Remember NPK functions"]
      },
      {
        id: "unit2.2",
        title: "Soil Management Practices",
        content: `
## Soil Management Practices

### Improving Soil Fertility

#### Animal Manures
- **Source**: Cattle, pig, chicken droppings
- **Benefits**: 
  - Adds organic matter
  - Improves soil structure
  - Slow-release nutrients
  - Increases water retention

#### Green Manure
- Crops grown and plowed into soil
- **Examples**: Mucuna, cowpea, crotalaria
- **Benefits**: Nitrogen fixation, organic matter

#### Compost
- **Ingredients**: Plant waste, manure, kitchen scraps
- **Process**: Decomposition by microorganisms
- **Benefits**: Improves all soil properties

### Soil Amendments

#### For Sandy Soil:
- Add clay to improve water retention
- Incorporate organic matter
- Use mulching

#### For Clay Soil:
- Add lime to improve structure
- Add gypsum for drainage
- Incorporate sand and organic matter

### Tillage Practices

| Type | Description | Best For |
|------|-------------|----------|
| Primary | Deep plowing | Breaking hard soil |
| Secondary | Harrowing | Seedbed preparation |
| Conservation | Minimal disturbance | Erosion control |

### Water Management

#### Irrigation Methods:
1. **Flood irrigation** - Traditional, high water use
2. **Furrow irrigation** - More efficient for row crops
3. **Drip irrigation** - Most efficient, precise delivery
4. **Sprinkler** - Good for large areas

#### Drainage:
- Removes excess water
- Prevents waterlogging
- Improves root growth
`,
        keyTerms: [
          { term: "Compost", definition: "Decomposed organic matter used as fertilizer" },
          { term: "Green manure", definition: "Crops grown to be plowed into soil for improvement" },
          { term: "Tillage", definition: "Mechanical manipulation of soil for cultivation" },
          { term: "Liming", definition: "Adding calcium compounds to reduce soil acidity" },
          { term: "Mulching", definition: "Covering soil surface to conserve moisture" }
        ],
        practiceQuestions: [
          "Explain how animal manure improves soil fertility",
          "Describe the benefits of using green manure crops",
          "Compare flood irrigation with drip irrigation"
        ],
        examTips: ["Know the advantages of each practice", "Link practices to soil problems they solve"]
      },
      {
        id: "unit2.3",
        title: "Soil Conservation Practices",
        content: `
## Soil Conservation Practices

### Why Conserve Soil?
- Maintains farm productivity
- Prevents erosion
- Protects water quality
- Ensures long-term sustainability

### Conservation Techniques

#### 1. Contour Farming
- Plowing along the contour lines
- Reduces water runoff
- Prevents rill formation

#### 2. Terracing
- Building stepped levels on slopes
- Controls water flow
- Creates flat planting areas

#### 3. Cover Crops
- Plants grown to protect soil
- Examples: Mucuna, clover, vetch
- Benefits: Erosion control, nitrogen fixation

#### 4. Mulching
- Covering soil with organic materials
- Types: Straw, leaves, coconut husks
- Benefits: Moisture retention, temperature control

#### 5. Strip Cropping
- Alternating strips of different crops
- Reduces erosion on slopes
- Improves biodiversity

#### 6. Windbreaks
- Rows of trees/shrubs to block wind
- Reduces wind erosion
- Provides habitat and products

### Comparison of Methods

| Method | Effectiveness | Cost | Labor |
|--------|--------------|------|-------|
| Contour farming | High | Low | Medium |
| Terracing | Very High | High | High |
| Cover crops | Medium | Low | Low |
| Mulching | Medium | Low | Medium |

### Local Conservation in Vanuatu
- Traditional fallow systems
- Agroforestry practices
- Organic matter recycling
- Community watershed management
`,
        keyTerms: [
          { term: "Contour farming", definition: "Plowing along elevation contours to reduce erosion" },
          { term: "Terracing", definition: "Creating stepped level areas on slopes" },
          { term: "Cover crops", definition: "Plants grown to protect and improve soil" },
          { term: "Strip cropping", definition: "Alternating strips of different crops" },
          { term: "Windbreak", definition: "Row of trees/shrubs to reduce wind erosion" }
        ],
        practiceQuestions: [
          "Describe how contour farming reduces soil erosion",
          "List four soil conservation techniques used in Vanuatu",
          "Discuss the advantages and disadvantages of terracing"
        ],
        examTips: ["Draw diagrams of conservation techniques", "Give local Vanuatu examples"]
      }
    ]
  },
  strand3: {
    title: "Plant and Animal Production",
    overview: "Understanding crop establishment, growth, harvest, and livestock management",
    image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Crop Establishment",
        content: `
## Crop Establishment

### Site Selection
Consider these factors when selecting a planting site:
- **Soil type** - Suitable for crop requirements
- **Drainage** - Adequate for crop needs
- **Slope** - Gentle slopes preferred
- **Access** - Easy for inputs and harvest
- **Water availability** - Irrigation potential

### Land Preparation

#### Tools and Equipment
| Tool | Use |
|------|-----|
| Hoe | Weeding, light cultivation |
| Spade | Digging, turning soil |
| Fork | Loosening compacted soil |
| Rake | Leveling, removing debris |
| Machete | Clearing vegetation |

#### Tillage Sequence
1. Clearing vegetation
2. Primary tillage (plowing)
3. Secondary tillage (harrowing)
4. Seedbed preparation
5. Marking rows

### Nursery Techniques

#### Traditional Nursery
- Simple beds on ground
- Minimal inputs
- Local materials
- Lower survival rates

#### Modern Nursery
- Raised beds or containers
- Controlled environment
- Higher quality seedlings
- Better survival rates

### Planting Methods

#### Direct Seeding
- Seeds planted directly in field
- Used for: maize, beans, peanuts
- Lower labor cost

#### Transplanting
- Seedlings raised then moved
- Used for: tomatoes, cabbage, peppers
- Better establishment

### Vegetative Propagation
- **Cuttings** - Cassava, kumala stems
- **Suckers** - Banana
- **Tubers** - Potato, taro
`,
        keyTerms: [
          { term: "Tillage", definition: "Mechanical preparation of soil for planting" },
          { term: "Nursery", definition: "Area where seedlings are raised before transplanting" },
          { term: "Transplanting", definition: "Moving seedlings from nursery to field" },
          { term: "Direct seeding", definition: "Planting seeds directly in the field" },
          { term: "Vegetative propagation", definition: "Plant reproduction using plant parts other than seeds" }
        ],
        practiceQuestions: [
          "List five factors to consider when selecting a planting site",
          "Compare traditional and modern nursery techniques",
          "Describe the steps in land preparation for crop establishment"
        ],
        examTips: ["Know the sequence of land preparation", "Give crop-specific examples"]
      },
      {
        id: "unit3.2",
        title: "Crop Growth and Development",
        content: `
## Crop Growth and Development

### Factors Affecting Plant Growth

#### 1. Biological Factors
- **Cultivar/Variety** - Genetic potential
- **Seed quality** - Germination rate, vigor
- **Pest/disease status** - Health condition

#### 2. Environmental Factors
- **Light** - Essential for photosynthesis
- **Temperature** - Affects growth rate
- **Humidity** - Influences transpiration
- **Rainfall** - Water supply

#### 3. Husbandry Factors
- **Watering** - Adequate moisture
- **Fertilizing** - Nutrient supply
- **Weeding** - Remove competition
- **Pruning** - Shape and productivity

### Growth Stages

| Stage | Description | Key Needs |
|-------|-------------|-----------|
| Germination | Seed sprouting | Moisture, warmth |
| Vegetative | Leaf/stem growth | Nitrogen, light |
| Flowering | Flower development | Phosphorus |
| Fruiting | Fruit/seed formation | Potassium |
| Maturity | Harvest readiness | Reduced water |

### Manipulating Growth

#### Pruning
- Removes unwanted growth
- Improves air circulation
- Increases fruit quality
- Shapes plant structure

#### Growth Hormones
- **Auxins** - Root development
- **Gibberellins** - Stem elongation
- **Cytokinins** - Cell division
- **Ethylene** - Fruit ripening

### Environmental Effects

| Factor | Low Level Effect | High Level Effect |
|--------|-----------------|-------------------|
| Light | Etiolation (pale, weak) | Scorching |
| Temperature | Slow growth | Heat stress |
| Humidity | Wilting | Disease risk |
| Water | Drought stress | Root rot |
`,
        keyTerms: [
          { term: "Cultivar", definition: "A plant variety produced by selective breeding" },
          { term: "Photosynthesis", definition: "Process by which plants convert light to food" },
          { term: "Pruning", definition: "Selective removal of plant parts to control growth" },
          { term: "Transpiration", definition: "Loss of water vapor from plant leaves" },
          { term: "Etiolation", definition: "Pale, weak growth due to insufficient light" }
        ],
        practiceQuestions: [
          "List three biological factors affecting crop growth",
          "Explain how pruning improves crop productivity",
          "Describe the effect of temperature on plant growth"
        ],
        examTips: ["Link environmental factors to plant responses", "Know the function of plant hormones"]
      },
      {
        id: "unit3.3",
        title: "Livestock Establishment and Management",
        content: `
## Livestock Establishment and Management

### Requirements for Young Animals

#### Housing Requirements
| Factor | Requirement |
|--------|------------|
| Floor space | Adequate for movement |
| Lighting | 12-16 hours for poultry |
| Ventilation | Fresh air circulation |
| Temperature | Species-specific |
| Bedding | Clean, dry material |

#### Feeding Young Livestock

**Poultry (Day-old to Grower)**
- Starter feed (21% protein)
- Grower feed (18% protein)
- Fresh clean water always

**Pigs (Birth to Weaning)**
- Colostrum first 24 hours
- Creep feed from week 1
- Weaning at 4-8 weeks

**Cattle (Birth to Weaning)**
- Colostrum within 6 hours
- Milk/milk replacer
- Introduce hay gradually

### Pearson's Square Method
For calculating feed rations:

1. Write desired protein % in center
2. Write feed protein % on left corners
3. Subtract diagonally
4. Results show mixing ratio

**Example:** Mix maize (9% protein) and soybean meal (44% protein) for 16% protein feed

### Livestock Husbandry Tasks

| Task | Purpose | Timing |
|------|---------|--------|
| Castration | Growth, behavior control | Early life |
| Eye-teeth cutting | Prevent teat injury (pigs) | Day 1-3 |
| Beak trimming | Prevent cannibalism | Early |
| Deworming | Parasite control | Regular |
| Vaccination | Disease prevention | Schedule |

### Growth Factors

**Biological:** Breed, genetics, health status
**Environmental:** Temperature, humidity, housing
**Husbandry:** Feeding, care, management
`,
        keyTerms: [
          { term: "Colostrum", definition: "First milk from mother, rich in antibodies" },
          { term: "Weaning", definition: "Transition from milk to solid feed" },
          { term: "Castration", definition: "Removal of male reproductive organs" },
          { term: "Creep feed", definition: "Supplementary feed for young animals while still nursing" },
          { term: "Feed conversion ratio", definition: "Amount of feed needed per unit of weight gain" }
        ],
        practiceQuestions: [
          "Describe housing requirements for young poultry",
          "Use Pearson's square to calculate a feed ration",
          "Explain the importance of colostrum for newborn livestock"
        ],
        examTips: ["Practice Pearson's square calculations", "Know species-specific requirements"]
      }
    ]
  },
  strand4: {
    title: "Crop and Animal Protection",
    overview: "Understanding pests, diseases, weeds, and their management",
    image: "https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Pests, Diseases and Weeds",
        content: `
## Types of Pests, Diseases and Weeds

### Parasites

#### Ecto-parasites (External)
- Live on the outside of host
- Examples: Ticks, lice, mites, fleas
- **Symptoms**: Skin irritation, hair loss, anemia

#### Endo-parasites (Internal)
- Live inside the host body
- Examples: Roundworms, tapeworms, flukes
- **Symptoms**: Weight loss, diarrhea, anemia

### Parasite Life Cycles

#### Complete Metamorphosis
Egg → Larva → Pupa → Adult
- Example: Flies, beetles

#### Incomplete Metamorphosis
Egg → Nymph → Adult
- Example: Ticks, grasshoppers

### Weeds in Vanuatu

#### Common Weeds
| Weed | Type | Problem |
|------|------|---------|
| Mile-a-minute | Invasive vine | Smothers crops |
| Elephant grass | Grass | Difficult to control |
| Lantana | Shrub | Toxic to livestock |
| Sensitive plant | Herb | Competes for nutrients |

#### How Weeds Spread
- Seeds (wind, water, animals)
- Vegetative fragments
- Human activities (contaminated tools)

### Common Livestock Diseases in Vanuatu

| Disease | Animal | Symptoms |
|---------|--------|----------|
| Newcastle | Poultry | Respiratory, neurological |
| Swine fever | Pigs | Fever, purple skin |
| Mastitis | Cattle | Swollen udder, abnormal milk |
| Coccidiosis | Poultry | Bloody diarrhea |

### Prevention Strategies
1. Biosecurity measures
2. Regular vaccination
3. Proper hygiene
4. Quarantine new animals
5. Pest control programs
`,
        keyTerms: [
          { term: "Ecto-parasite", definition: "A parasite that lives on the external surface of its host" },
          { term: "Endo-parasite", definition: "A parasite that lives inside its host's body" },
          { term: "Metamorphosis", definition: "The biological process of transformation in an insect's life cycle" },
          { term: "Invasive weed", definition: "Non-native plant species that spreads rapidly and causes harm" },
          { term: "Host", definition: "An organism that harbors a parasite" }
        ],
        practiceQuestions: [
          "Distinguish between ecto-parasites and endo-parasites",
          "Describe the complete life cycle of an insect pest",
          "Name three invasive weeds found in Vanuatu and their impacts"
        ],
        examTips: ["Draw and label life cycle diagrams", "Know symptoms of common diseases"]
      },
      {
        id: "unit4.2",
        title: "Pest and Disease Management",
        content: `
## Management of Pests and Diseases

### Control Methods

#### 1. Biological Control
Using living organisms to control pests
- **Predators**: Ladybugs eat aphids
- **Parasitoids**: Wasps parasitize caterpillars
- **Pathogens**: Bacteria kill insects

**Advantages:**
- Environmentally friendly
- Self-sustaining
- No chemical residues

**Disadvantages:**
- Slow acting
- May not be effective alone
- Requires expertise

#### 2. Chemical Control
Using pesticides to kill pests
- **Insecticides** - Kill insects
- **Fungicides** - Kill fungi
- **Herbicides** - Kill weeds
- **Acaricides** - Kill mites/ticks

**Safe Pesticide Use:**
1. Read label instructions
2. Wear protective equipment
3. Mix correct concentration
4. Apply at right time
5. Store safely
6. Dispose properly

#### 3. Cultural Control
Farm management practices that reduce pests
- Crop rotation
- Resistant varieties
- Proper spacing
- Field sanitation
- Timing of planting

### Comparison of Methods

| Method | Cost | Environmental Impact | Effectiveness |
|--------|------|---------------------|---------------|
| Biological | Low | Positive | Moderate |
| Chemical | High | Negative | High |
| Cultural | Low | Positive | Moderate |

### Integrated Pest Management (IPM)

**Definition:** A sustainable approach combining multiple control methods

**IPM Principles:**
1. Prevention first
2. Monitor pest levels
3. Set action thresholds
4. Use multiple tactics
5. Evaluate effectiveness
`,
        keyTerms: [
          { term: "Biological control", definition: "Using natural enemies to control pest populations" },
          { term: "Pesticide", definition: "Chemical substance used to kill pests" },
          { term: "Cultural control", definition: "Farm practices that reduce pest problems" },
          { term: "IPM", definition: "Integrated Pest Management - combining multiple control methods" },
          { term: "Action threshold", definition: "Pest level at which control measures should begin" }
        ],
        practiceQuestions: [
          "Compare biological and chemical pest control methods",
          "Describe safe procedures for pesticide application",
          "Explain the principles of Integrated Pest Management"
        ],
        examTips: ["Know advantages and disadvantages of each method", "Emphasize IPM benefits"]
      }
    ]
  },
  strand5: {
    title: "Farm Management and Economics",
    overview: "Understanding costs, returns, markets, and farm management principles",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Costs, Returns and Profit",
        content: `
## Costs, Returns and Profit

### Types of Costs

#### Establishment Costs (Fixed)
One-time costs to start the enterprise
- Land preparation
- Fencing
- Building construction
- Equipment purchase
- Initial stock

#### Production Costs (Variable)
Ongoing costs that vary with production
- Seeds/planting material
- Fertilizers
- Pesticides
- Labor
- Feed
- Veterinary services

### Calculating Costs

**Total Cost = Establishment Cost + Production Cost**

**Example: Vegetable Garden (1000m²)**

| Establishment Costs | Amount (VT) |
|-------------------|-------------|
| Land clearing | 5,000 |
| Fencing | 15,000 |
| Tools | 10,000 |
| **Total** | **30,000** |

| Production Costs | Amount (VT) |
|-----------------|-------------|
| Seeds | 3,000 |
| Fertilizer | 5,000 |
| Labor | 20,000 |
| **Total** | **28,000** |

### Returns and Revenue
**Returns** = Total income from sales

**Calculating Returns:**
Revenue = Quantity Sold × Price per Unit

### Profit Calculation
**Profit = Total Returns - Total Costs**

**Example:**
- Revenue from vegetable sales: 80,000 VT
- Total costs: 58,000 VT
- **Profit: 22,000 VT**

### Factors Increasing Production Costs
1. Inflation
2. Transportation costs
3. Technology requirements
4. Labor shortages
5. Market price volatility
6. Climate-related losses
`,
        keyTerms: [
          { term: "Establishment cost", definition: "One-time costs to start a farm enterprise" },
          { term: "Production cost", definition: "Ongoing costs that vary with production level" },
          { term: "Returns", definition: "Total income received from selling products" },
          { term: "Profit", definition: "Returns minus total costs" },
          { term: "Financial record", definition: "Documentation of all farm income and expenses" }
        ],
        practiceQuestions: [
          "Calculate establishment costs for a small poultry project",
          "Distinguish between establishment and production costs",
          "Explain three reasons why production costs are increasing"
        ],
        examTips: ["Show all calculation steps clearly", "Use realistic local prices"]
      },
      {
        id: "unit5.2",
        title: "Markets and Farm Management",
        content: `
## Markets and Farm Management

### Market Concepts

#### Key Definitions
- **Market**: Place where buyers and sellers exchange goods
- **Demand**: Quantity buyers want at a given price
- **Supply**: Quantity sellers offer at a given price
- **Price**: Value at which exchange occurs

### Market Forces

#### Law of Demand
As price increases, quantity demanded decreases

#### Law of Supply
As price increases, quantity supplied increases

#### Equilibrium Price
Where supply equals demand

### Factors Influencing Markets

| Factor | Effect on Price |
|--------|----------------|
| High demand | Price increases |
| High supply | Price decreases |
| Poor quality | Price decreases |
| Seasonality | Price fluctuates |
| Competition | Price decreases |
| Transport costs | Price increases |

### Farm Management

#### Key Resources (Factors of Production)
1. **Land** - Natural resources
2. **Labor** - Human work
3. **Capital** - Money and equipment

#### The Manager's Role
- Planning operations
- Organizing resources
- Directing activities
- Controlling outcomes

### Record Keeping

**Why Keep Records?**
- Track income and expenses
- Monitor performance
- Plan future activities
- Secure loans
- Pay taxes accurately

**Essential Records:**
- Sales records
- Purchase receipts
- Production data
- Labor records
- Asset inventory
`,
        keyTerms: [
          { term: "Market", definition: "A place or system where buyers and sellers exchange goods" },
          { term: "Demand", definition: "The quantity of a product that buyers are willing to purchase" },
          { term: "Supply", definition: "The quantity of a product that sellers are willing to offer" },
          { term: "Record keeping", definition: "Systematic documentation of farm activities and finances" },
          { term: "Factors of production", definition: "Resources needed for production: land, labor, capital" }
        ],
        practiceQuestions: [
          "Explain how supply and demand affect market price",
          "List three factors influencing the market for agricultural products",
          "Discuss the importance of record keeping in farm management"
        ],
        examTips: ["Draw supply and demand curves", "Give practical examples from local markets"]
      }
    ]
  }
};

// Year 12 Agriculture Student Notes
export const year12AgricultureStudentNotes = {
  strand1: {
    title: "Sustainable Farm Ecosystem",
    overview: "Advanced concepts in integrated farm management and traditional farming systems",
    image: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Integrated Farm Management",
        content: `
## Integrated Farm Management

### Definition
Integrated Farm Management (IFM) is a whole-farm approach that balances profitability, environmental care, and social responsibility.

### Farm Management Systems

| System | Description | Intensity |
|--------|-------------|-----------|
| Extensive | Large area, low inputs | Low |
| Semi-intensive | Moderate area and inputs | Medium |
| Intensive | Small area, high inputs | High |

### Comparison

| Factor | Extensive | Intensive |
|--------|-----------|-----------|
| Land use | Large | Small |
| Labor | Low | High |
| Capital | Low | High |
| Yield per hectare | Low | High |
| Environmental impact | Low | High |

### IFM Principles
1. **Organization and planning** - Set clear goals
2. **Soil management** - Maintain fertility
3. **Crop protection** - Minimize chemical use
4. **Energy efficiency** - Reduce waste
5. **Water management** - Conserve resources
6. **Animal welfare** - Ethical treatment

### Sustainable Husbandry Practices
- Rotational grazing
- Appropriate stocking rates
- Proper nutrition
- Disease prevention
- Humane handling

### Environmental and Economic Balance
IFM delivers sustainability by:
- Reducing input costs
- Improving soil health
- Protecting biodiversity
- Ensuring long-term profitability
`,
        keyTerms: [
          { term: "Integrated Farm Management", definition: "Whole-farm approach balancing profit, environment, and social responsibility" },
          { term: "Extensive farming", definition: "Large-scale farming with low inputs per unit area" },
          { term: "Intensive farming", definition: "High-input farming on smaller areas" },
          { term: "Animal welfare", definition: "Ethical treatment ensuring animals' physical and psychological well-being" },
          { term: "Sustainability", definition: "Meeting present needs without compromising future generations" }
        ],
        practiceQuestions: [
          "Compare extensive and intensive farming systems",
          "Explain how IFM contributes to economic performance",
          "Describe sustainable husbandry practices for livestock"
        ],
        examTips: ["Use local examples of different farming systems", "Link economic and environmental benefits"]
      }
    ]
  },
  strand2: {
    title: "Soil Management",
    overview: "Improving soil properties and agronomic practices",
    image: "https://images.unsplash.com/photo-1558906117-a2e1e50e0e04?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Improving Soil Properties",
        content: `
## Improving Soil Properties

### Physical Property Improvements

#### Granulation (Good Tilth)
- Achieved through organic matter addition
- Creates stable soil aggregates
- Improves workability

#### Aeration
- Proper tillage practices
- Avoid compaction
- Use cover crops

#### Root Penetration
- Deep tillage for hardpan
- Organic matter improves structure
- Proper drainage

#### Water Holding Capacity
- Add organic matter
- Use mulching
- Improve soil structure

### Using Organic Matter and Compost

**Benefits:**
1. Improves soil structure
2. Increases water retention
3. Provides slow-release nutrients
4. Feeds soil organisms
5. Buffers soil pH

**Application Rates:**
- Compost: 5-10 tons/hectare
- Well-rotted manure: 10-20 tons/hectare

### Soil Biological Properties

#### Assessing Biological Health
- Earthworm counts
- Microbial activity tests
- Organic matter levels
- Soil respiration rates

#### Soil Organisms and Their Roles

| Organism | Role |
|----------|------|
| Earthworms | Aeration, organic matter breakdown |
| Bacteria | Nutrient cycling, nitrogen fixation |
| Fungi | Decomposition, nutrient transfer |
| Protozoa | Nutrient release, bacteria control |

### Tillage for Desired Structure
- Primary tillage: Break up soil
- Secondary tillage: Create fine seedbed
- Minimum tillage: Reduce erosion
`,
        keyTerms: [
          { term: "Tilth", definition: "Physical condition of soil in relation to plant growth" },
          { term: "Granulation", definition: "Formation of soil aggregates" },
          { term: "Soil aeration", definition: "The exchange of gases between soil and atmosphere" },
          { term: "Hardpan", definition: "A compacted layer of soil that restricts root growth" },
          { term: "Soil respiration", definition: "Production of CO2 by soil organisms" }
        ],
        practiceQuestions: [
          "Explain how organic matter improves soil properties",
          "Describe methods to improve soil aeration",
          "List four types of soil organisms and their roles"
        ],
        examTips: ["Connect soil properties to crop performance", "Know specific improvement techniques"]
      }
    ]
  },
  strand3: {
    title: "Plant and Animal Production",
    overview: "Seed production, crop management, livestock breeding and value adding",
    image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Seed Production and Crop Management",
        content: `
## Seed Production

### Why Produce Seeds?
- Ensure supply of planting material
- Maintain genetic quality
- Reduce costs
- Local adaptation

### Seed Production Process

#### 1. Selection
Choose plants with:
- High yield
- Disease resistance
- Good quality
- True to type

#### 2. Harvesting
- Harvest at full maturity
- Avoid damage
- Dry conditions preferred

#### 3. Cleaning
- Remove debris
- Separate damaged seeds
- Grade by size

#### 4. Drying
- Reduce moisture to 10-12%
- Prevent mold growth
- Use shade drying or dryers

#### 5. Storage
- Cool, dry conditions
- Airtight containers
- Protection from pests

#### 6. Treatment
- Fungicide coating
- Insecticide treatment
- Inoculation (legumes)

## Crop Management

### Timeline for Crop Production

| Stage | Activities |
|-------|-----------|
| Pre-planting | Site selection, land preparation |
| Establishment | Seed treatment, sowing, transplanting |
| Growth | Fertilizing, weeding, irrigation |
| Flowering | Pest control, reduced nitrogen |
| Harvest | Timing, handling, storage |

### Management Practices

**At Soil Level:**
- Deep tillage for root penetration
- Drainage for optimal moisture
- Organic matter for structure

**During Growth:**
- Regular weeding
- Appropriate fertilization
- Pest and disease monitoring
- Pruning where needed
`,
        keyTerms: [
          { term: "Seed selection", definition: "Choosing the best plants for seed production" },
          { term: "Seed cleaning", definition: "Removing impurities and damaged seeds" },
          { term: "Seed treatment", definition: "Applying chemicals or inoculants to protect seeds" },
          { term: "Crop management", definition: "All activities to care for crops from planting to harvest" },
          { term: "Inoculation", definition: "Treating seeds with beneficial bacteria" }
        ],
        practiceQuestions: [
          "Describe the complete process of seed production",
          "Explain the importance of seed drying and storage",
          "Develop a crop management timeline for a selected crop"
        ],
        examTips: ["Know proper moisture levels for seed storage", "Link activities to crop growth stages"]
      }
    ]
  },
  strand4: {
    title: "Crop and Animal Protection",
    overview: "Pest control economics and appropriate management measures",
    image: "https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Economics of Pest and Disease Control",
        content: `
## Economics of Controlling Pests and Diseases

### Economic Impact of Pests

#### Direct Losses
- Reduced yield
- Lower quality
- Crop destruction
- Animal death

#### Indirect Losses
- Control costs
- Labor for management
- Market restrictions
- Treatment expenses

### Estimating Economic Losses

**Formula:**
Economic Loss = (Yield Loss × Price) + Control Costs

**Example:**
- Expected yield: 2000 kg
- Actual yield: 1500 kg (pest damage)
- Price: 100 VT/kg
- Loss = (500 × 100) = 50,000 VT

### Cost-Effective Control

| Method | Cost | Effectiveness | Recommendation |
|--------|------|--------------|----------------|
| Cultural | Low | Moderate | Always use |
| Biological | Medium | Variable | Use when available |
| Chemical | High | High | Last resort |
| IPM | Medium | High | Best approach |

### Economic Threshold
The pest density at which control cost equals damage cost

**When to Act:**
- Pest population reaches threshold
- Damage exceeds control cost
- Prevention is more economical

### IPM Economics
Integrated Pest Management is economically superior because:
1. Reduces chemical costs
2. Preserves beneficial insects
3. Prevents resistance development
4. Maintains market access
5. Protects environment
`,
        keyTerms: [
          { term: "Economic threshold", definition: "Pest level at which control costs equal damage costs" },
          { term: "Economic loss", definition: "Financial impact of pest or disease damage" },
          { term: "Cost-benefit analysis", definition: "Comparing control costs to benefits gained" },
          { term: "Direct loss", definition: "Immediate impact on yield or quality" },
          { term: "Indirect loss", definition: "Secondary costs from pest damage" }
        ],
        practiceQuestions: [
          "Calculate economic loss from pest damage",
          "Explain the concept of economic threshold",
          "Compare the economics of different pest control methods"
        ],
        examTips: ["Show all calculation steps", "Justify control recommendations economically"]
      }
    ]
  },
  strand5: {
    title: "Farm Management and Economics",
    overview: "Cash flow analysis, gross margins, and factors affecting primary production",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Cash Flow and Gross Margin Analysis",
        content: `
## Cash Flow and Gross Margin Analysis

### Cash Flow

**Definition:** The movement of money into and out of a business

**Components:**
- Cash inflows (sales, loans, grants)
- Cash outflows (purchases, wages, loan repayments)

**Monthly Cash Flow Statement:**

| Month | Inflows | Outflows | Balance |
|-------|---------|----------|---------|
| Jan | 50,000 | 30,000 | 20,000 |
| Feb | 20,000 | 25,000 | -5,000 |
| Mar | 80,000 | 40,000 | 40,000 |

### Gross Margin Analysis

**Formula:**
Gross Margin = Gross Income - Variable Costs

**Example: Cabbage Enterprise (1000m²)**

| Gross Income | Amount (VT) |
|-------------|-------------|
| 500 heads × 200 VT | 100,000 |

| Variable Costs | Amount (VT) |
|---------------|-------------|
| Seedlings | 5,000 |
| Fertilizer | 8,000 |
| Pesticides | 3,000 |
| Labor | 15,000 |
| **Total** | **31,000** |

**Gross Margin = 100,000 - 31,000 = 69,000 VT**

### Budgeting

**Simple Budget Format:**

| Item | Projected | Actual | Variance |
|------|-----------|--------|----------|
| Sales | 100,000 | 95,000 | -5,000 |
| Costs | 60,000 | 62,000 | +2,000 |
| Profit | 40,000 | 33,000 | -7,000 |

### Comparing Enterprises

Use gross margin analysis to:
- Compare profitability of different crops
- Decide which enterprise to expand
- Identify most efficient use of resources
`,
        keyTerms: [
          { term: "Cash flow", definition: "Movement of money into and out of a business" },
          { term: "Gross margin", definition: "Gross income minus variable costs" },
          { term: "Variable costs", definition: "Costs that change with level of production" },
          { term: "Budget", definition: "A financial plan for expected income and expenses" },
          { term: "Variance", definition: "Difference between budgeted and actual amounts" }
        ],
        practiceQuestions: [
          "Prepare a cash flow statement for a farm enterprise",
          "Calculate gross margin for a given enterprise",
          "Explain the purpose of budgeting in farm management"
        ],
        examTips: ["Practice calculations with realistic figures", "Interpret results for decision-making"]
      }
    ]
  }
};

// Year 13 Agriculture Student Notes
export const year13AgricultureStudentNotes = {
  strand1: {
    title: "Sustainable Farm Ecosystem",
    overview: "Global environmental issues and sustainable management of farm ecosystems",
    image: "https://images.unsplash.com/photo-1560493676-04071c5f467b?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Global Environmental Issues",
        content: `
## Global Environmental Issues in Agriculture

### Major Issues Affecting Vanuatu

#### 1. Climate Change
- Rising temperatures
- Changing rainfall patterns
- More frequent extreme weather
- Sea level rise

**Effects on Agriculture:**
- Altered growing seasons
- New pest and disease patterns
- Water scarcity
- Crop failures

#### 2. Introduction of New Species
**Advantages:**
- Higher yields
- Disease resistance
- Market opportunities

**Disadvantages:**
- May become invasive
- Genetic pollution
- Unknown long-term effects

#### 3. Genetic Engineering
**Definition:** Manipulation of organism's genes using biotechnology

**Issues:**
- Biodiversity concerns
- Corporate control of seeds
- Unknown health effects
- Traditional variety loss

### Genetic Resource Conservation

**Why Conserve?**
- Preserve biodiversity
- Maintain breeding options
- Cultural heritage
- Climate adaptation

**Methods:**
- Seed banks
- In-situ conservation
- Farmer seed saving
- Protected areas

### Case Study Approach

**Structure:**
1. **Aim**: What you're investigating
2. **Rationale**: Why it matters
3. **Background**: Context information
4. **Method**: How you'll investigate
5. **Results**: What you found
6. **Analysis**: Interpretation of data
7. **Conclusion**: Key findings
8. **Recommendations**: Suggested actions
`,
        keyTerms: [
          { term: "Climate change", definition: "Long-term changes in temperature and weather patterns" },
          { term: "Genetic engineering", definition: "Direct manipulation of an organism's genes" },
          { term: "Genetic resource", definition: "Genetic material with value for food and agriculture" },
          { term: "Invasive species", definition: "Non-native species that cause ecological harm" },
          { term: "In-situ conservation", definition: "Conservation in natural habitats" }
        ],
        practiceQuestions: [
          "Explain three effects of climate change on Vanuatu agriculture",
          "Discuss advantages and disadvantages of introducing new crop varieties",
          "Design a case study to assess a local agricultural issue"
        ],
        examTips: ["Use current examples from Vanuatu", "Follow case study structure carefully"]
      }
    ]
  },
  strand2: {
    title: "Soil Management",
    overview: "Soil productivity assessment and conservation economics",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Soil Productivity and Conservation Economics",
        content: `
## Soil Productivity

### Definition
Soil productivity is the capacity of soil to produce crops under defined management conditions.

### Factors Affecting Productivity

| Factor | Effect |
|--------|--------|
| Soil depth | Deeper = more root space |
| Texture | Affects water/nutrient holding |
| Structure | Influences aeration, drainage |
| pH | Nutrient availability |
| Organic matter | Overall fertility indicator |
| Drainage | Prevents waterlogging |

### Assessment Methods

#### 1. Physical Assessment
- Profile description
- Texture analysis
- Structure evaluation
- Drainage observation

#### 2. Chemical Testing
- pH measurement
- Nutrient levels (NPK)
- Organic matter content
- Cation exchange capacity

#### 3. Biological Assessment
- Earthworm counts
- Microbial activity
- Root development observation

### Soil Conservation Economics

#### Costs of Erosion
- Yield reduction
- Nutrient loss
- Replanting costs
- Infrastructure damage
- Water quality decline

#### Benefits of Conservation

| Benefit Type | Examples |
|-------------|----------|
| Economic | Sustained yields, reduced input costs |
| Agronomic | Better soil health, water retention |
| Environmental | Cleaner water, biodiversity |

### Cost-Benefit Analysis

**Example: Terracing Project**

| Costs | Amount (VT) |
|-------|-------------|
| Construction | 100,000 |
| Maintenance | 5,000/year |

| Benefits | Amount (VT) |
|----------|-------------|
| Yield increase | 20,000/year |
| Reduced erosion | 10,000/year |

**Payback Period:** 100,000 ÷ 25,000 = 4 years
`,
        keyTerms: [
          { term: "Soil productivity", definition: "The capacity of soil to produce crops" },
          { term: "Cation exchange capacity", definition: "Soil's ability to hold and exchange nutrients" },
          { term: "Payback period", definition: "Time needed to recover an investment through benefits" },
          { term: "Profile", definition: "Vertical section of soil showing different layers" },
          { term: "Conservation economics", definition: "Economic analysis of soil conservation practices" }
        ],
        practiceQuestions: [
          "List factors that affect soil productivity",
          "Describe methods for assessing soil biological health",
          "Calculate the payback period for a conservation investment"
        ],
        examTips: ["Know how to interpret soil test results", "Use economic analysis to justify conservation"]
      }
    ]
  },
  strand3: {
    title: "Plant and Animal Production",
    overview: "Crop and livestock breeding, schedule of operations, and marketing",
    image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Crop and Livestock Breeding",
        content: `
## Crop Breeding

### Breeding Methods

#### 1. Selection
- Choose best individuals
- Grow seeds from these plants
- Repeat over generations
- **Example**: Yam variety selection

#### 2. Hybridization
- Cross two different varieties
- Combine desirable traits
- **Example**: Hybrid coconut

#### 3. Line Breeding
- Repeated crossing within a family
- Maintains specific traits
- Concentrates genes

### Breeding Program Design

**Steps:**
1. Set objectives (what traits to improve)
2. Select parent materials
3. Make crosses
4. Grow and evaluate progeny
5. Select superior individuals
6. Test performance
7. Release new variety

## Livestock Breeding

### Breeding Systems

#### Pure Breeding
- Mating within same breed
- Maintains breed characteristics
- May reduce vigor

#### Crossbreeding
- Mating different breeds
- Hybrid vigor (heterosis)
- Combines traits

#### Line Breeding
- Mating related individuals
- Concentrates desirable genes
- Risk of inbreeding depression

### Artificial Insemination (AI)

**Advantages:**
- Access to superior genetics
- Disease control
- Cost effective
- No need to keep bull

**Disadvantages:**
- Technical skill needed
- Equipment required
- Timing critical
- Success rate varies

### Breeding Records
- Parentage information
- Birth dates and weights
- Growth performance
- Reproductive history
- Health records
`,
        keyTerms: [
          { term: "Hybridization", definition: "Crossing two genetically different plants or animals" },
          { term: "Heterosis", definition: "Increased vigor in offspring from crossbreeding (hybrid vigor)" },
          { term: "Selection", definition: "Choosing best individuals for breeding" },
          { term: "Artificial insemination", definition: "Introduction of semen into female without natural mating" },
          { term: "Inbreeding depression", definition: "Reduced fitness from mating closely related individuals" }
        ],
        practiceQuestions: [
          "Design a simple crop breeding program for taro",
          "Compare pure breeding and crossbreeding systems",
          "Explain the advantages and disadvantages of artificial insemination"
        ],
        examTips: ["Draw breeding diagrams clearly", "Know which system suits different situations"]
      }
    ]
  },
  strand4: {
    title: "Crop and Animal Protection",
    overview: "Sustainable pest management, biosecurity regulations, and trade impacts",
    image: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Biosecurity and Trade Regulations",
        content: `
## Biosecurity Regulations

### Definition
Biosecurity regulations are laws and procedures designed to prevent the introduction and spread of harmful organisms.

### Quarantine Laws

#### Import Controls
- Inspection of goods
- Treatment requirements
- Prohibited items lists
- Certification requirements

#### Export Controls
- Phytosanitary certificates
- Quality standards
- Treatment before export
- Documentation

### Vanuatu Biosecurity
**Biosecurity Vanuatu** administers:
- Border control
- Pest surveillance
- Emergency response
- Import permits

### Regional Harmonization

**Why Harmonize Laws?**
- Facilitate trade
- Consistent standards
- Efficient enforcement
- Regional cooperation

**Pacific Plant Protection Organisation (PPPO)**
- Sets regional standards
- Coordinates pest management
- Facilitates trade

## Effects of Pests on Trade

### Trade Barriers
Pests and diseases create:
- Market restrictions
- Treatment requirements
- Certification costs
- Rejected shipments

### Economic Impacts

| Impact | Example |
|--------|---------|
| Market loss | Ban on exports |
| Increased costs | Treatment requirements |
| Price reduction | Lower quality grades |
| Reputation damage | Loss of buyer confidence |

### Good Reporting Systems

**Benefits:**
- Early pest detection
- Rapid response
- Trade confidence
- Regional cooperation
`,
        keyTerms: [
          { term: "Biosecurity", definition: "Measures to prevent introduction of harmful organisms" },
          { term: "Quarantine", definition: "Isolation to prevent spread of pests or diseases" },
          { term: "Phytosanitary certificate", definition: "Document certifying plant health status" },
          { term: "Harmonization", definition: "Making regulations consistent across countries" },
          { term: "Pest surveillance", definition: "Systematic monitoring for pest presence" }
        ],
        practiceQuestions: [
          "Explain the purpose of quarantine laws",
          "Describe how pests and diseases affect agricultural trade",
          "Discuss the benefits of harmonizing quarantine laws in the Pacific region"
        ],
        examTips: ["Know specific Vanuatu biosecurity requirements", "Link regulations to trade impacts"]
      }
    ]
  },
  strand5: {
    title: "Farm Management and Economics",
    overview: "Profitability analysis, trade regulations, and agricultural policies",
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Profitability and Trade",
        content: `
## Profitability in Farming

### Financial Tools

#### 1. Gross Margin Analysis
Gross Income - Variable Costs

#### 2. Net Profit Analysis
Gross Margin - Fixed Costs

#### 3. Partial Budgeting
Analyzing changes to the farm business

#### 4. Cash Flow Analysis
Tracking money in and out

#### 5. Break-even Analysis
Production level where costs equal returns

### Partial Budgeting

**Use when considering changes like:**
- New enterprise
- Different variety
- New technology
- Changed practice

**Format:**
| Added Returns | Added Costs |
|---------------|-------------|
| New income | New expenses |
| Reduced costs | Reduced income |
| **Total A** | **Total B** |

**Net Benefit = Total A - Total B**

## Trade in Agricultural Products

### Marketing Channels

**Traditional:**
Farmer → Local Market → Consumer

**Commercial:**
Farmer → Collector → Wholesaler → Retailer → Consumer

**Export:**
Farmer → Exporter → Importer → Foreign Market

### World Trade Organization (WTO)

**Functions:**
- Sets global trade rules
- Resolves disputes
- Promotes free trade
- Provides technical assistance

**Benefits to Vanuatu:**
- Market access
- Technical support
- Dispute settlement
- Trade capacity building

### National Agricultural Policies

**Government Roles:**
- Price support
- Subsidies
- Trade regulations
- Quality standards
- Research and extension
`,
        keyTerms: [
          { term: "Partial budgeting", definition: "Analysis of financial impact of a proposed change" },
          { term: "Break-even analysis", definition: "Finding production level where costs equal revenue" },
          { term: "Marketing channel", definition: "Path products take from producer to consumer" },
          { term: "WTO", definition: "World Trade Organization - sets global trade rules" },
          { term: "Subsidy", definition: "Government financial support to farmers" }
        ],
        practiceQuestions: [
          "Use partial budgeting to analyze a proposed farm change",
          "Describe marketing channels for an agricultural product",
          "Explain the role of WTO in agricultural trade"
        ],
        examTips: ["Practice partial budget calculations", "Know WTO principles and benefits"]
      }
    ]
  }
};

export default {
  year11: year11AgricultureStudentNotes,
  year12: year12AgricultureStudentNotes,
  year13: year13AgricultureStudentNotes
};
